import { ChevronDown, Search } from '@huspy/briks-icons';
import {
  Button,
  Chip, Flex, Menu,
  Switch,
  Text,
  Input,
} from '@huspy/briks-web';
import React, {
  useState, ChangeEvent, useMemo, KeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@mantine/core';
import { debounce } from '@shared/utils';
import ScrollArea from '../ScrollArea';

type DataItem = {
  value: string;
  label?: string;
  node?: () => JSX.Element;
};

type Props = {
  data: Array<DataItem>;
  initialDataValues: Array<string>;
  contentHeight?: string;
  onApply: (data: Array<string>) => void;
  doNotCloseWhenApply?: boolean;
  showAppliedData?: boolean;
  showSearch?: boolean;
  isActive: boolean;
  dataTest: string;
  label: string;
  header: string;
  children?: React.ReactNode;
  applyOnSelect?: boolean;
};

const handleOnKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
  if ([' ', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
    e.stopPropagation();
  }
};

const MultiSelect = ({
  data,
  initialDataValues,
  contentHeight = '300px',
  onApply,
  doNotCloseWhenApply,
  showAppliedData,
  showSearch,
  isActive,
  dataTest,
  label,
  header,
  children,
  applyOnSelect,
}: Props) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<string[]>(initialDataValues);

  const [searchText, setSearchText] = useState('');

  const debouncedSearch = debounce((value: string) => {
    setSearchText(value);
  }, 100);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  const handleCheckboxOnChange = (value: string) => {
    let newFilters: string[] = filters;

    newFilters = filters.includes(value) ? newFilters.filter((val) => val !== value) : [...newFilters, value];

    setFilters(newFilters);

    if (applyOnSelect) {
      onApply(newFilters);
    }
  };

  const isEveryFilterChecked = filters.length === data?.length;

  const handleSelectAll = () => {
    const newFilters = isEveryFilterChecked ? [] : data.map((item) => item.value);
    setFilters(newFilters);

    if (applyOnSelect) {
      onApply(newFilters);
    }
  };

  const appliedData = data.filter((dataItem) => initialDataValues.includes(dataItem.value));

  const appliedDataLabel = `${appliedData[0]?.label}${appliedData.length > 1 ? ` + ${appliedData.length - 1}` : ''}`;

  const dataToRender = useMemo(() => {
    if (searchText) {
      return data.filter((item) => item.label?.toLowerCase().includes(searchText.toLowerCase()));
    }
    return data;
  }, [data, searchText]);

  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <Chip
          variant={ isActive ? 'neutral' : 'glassy' }
          size='sm'
          data-test={ dataTest }
          style={ { cursor: 'pointer' } }
          py='2'
          px='3'
          borderWidth='1px'
          borderStyle='solid'
          borderColor={ isActive ? 'border-primary' : 'border-secondary' }
        >
          <Text size='md' display='flex' alignItems='center' gap='1'>
            { showAppliedData && appliedData.length > 0 ? appliedDataLabel : label }
            <ChevronDown />
          </Text>
        </Chip>
      </Menu.Trigger>

      <Menu.Positioner>
        <Menu.Content borderRadius='radius-4'>
          <Flex
            gap={ 6 }
            direction='column'
            paddingX='4'
            paddingY='5'
          >
            <Text color='utility-blue-600' size='md' weight='semiBold'>{ header }</Text>
            <Flex align='center' justify='space-between'>
              <Text size='sm'>{t('common.forms.multiSelect.selectAll')}</Text>
              <Switch onChange={ handleSelectAll } checked={ isEveryFilterChecked } data-testid='select-all-button' />
            </Flex>
            {
              showSearch ? (
                <Input
                  placeholder={ t('common.forms.multiSelect.search') }
                  bg='bg-secondary'
                  borderColor='border-secondary'
                  backdropFilter='blur(32px);'
                  icon={ <Search width={ 16 } height={ 16 } color='#6B7280' /> }
                  iconPosition='left'
                  defaultValue={ searchText }
                  onChange={ handleSearch }
                  onKeyDown={ handleOnKeyDown }
                  spellCheck={ false }
                  size='xs'
                  width='100%'
                  css={ { paddingLeft: '9', fontSize: 'sm' } }
                  data-test='search-bank-input'
                  data-testid='search-bank-input'
                />
              ) : null
            }
            <ScrollArea h={ contentHeight } type='hover'>
              <Menu.ItemGroup gap='2'>
                {dataToRender?.map((item) => (
                  <Flex key={ item.value } alignItems='center' gap={ 2 }>
                    <Checkbox
                      checked={ filters.includes(item.value) }
                      value={ item.value }
                      id={ item.value }
                      onChange={ () => handleCheckboxOnChange(item.value) }
                    />
                    <label htmlFor={ item.value }>
                      {item.node ? item.node?.() : (
                        <Text
                          htmlFor={ item.value }
                          size='sm'
                          style={ { cursor: 'pointer' } }
                        >
                          {item.label}
                        </Text>
                      )}
                    </label>
                  </Flex>
                ))}
                {dataToRender?.length === 0
                && (
                  <Text
                    size='sm'
                    color='fg-secondary'
                    textOverflow='ellipsis'
                    whiteSpace='nowrap'
                    overflow='hidden'
                    maxWidth='220px'
                  >
                    {t('common.forms.multiSelect.noResults')}
                    {' '}
                    {searchText ? `${t('common.forms.multiSelect.for')} "${searchText}"` : ''}
                  </Text>
                )}
                { children }
              </Menu.ItemGroup>
            </ScrollArea>
          </Flex>
          { !applyOnSelect
          && (
            <>
              <Menu.Separator />
              <Menu.Item
                value='controls'
                paddingX='4'
                paddingY='2'
                justifyContent='flex-end'
                css={ {
                  _hover: {
                    backgroundColor: 'transparent',
                    cursor: 'initial',
                  },
                } }
              >
                <Button
                  data-testid='apply-button'
                  size='sm'
                  onClick={ (e) => {
                    if (doNotCloseWhenApply) {
                      e.stopPropagation();
                    }
                    onApply(filters);
                  } }
                >
                  {t('common.forms.multiSelect.apply')}
                </Button>
              </Menu.Item>
            </>
          )}
        </Menu.Content>
      </Menu.Positioner>
    </Menu.Root>
  );
};

export default MultiSelect;
